import "dayjs/locale/de";
import "dayjs/locale/en";

import {
  Add,
  AssessmentOutlined,
  ListAltRounded,
  Lock,
  Logout,
  MailOutline,
  NotificationsOutlined,
  PublishedWithChanges,
  SentimentSatisfiedAlt,
  SettingsOutlined,
  SpaceDashboardOutlined,
} from "@mui/icons-material";
import {
  Notification,
  NotificationChannel,
  NotificationsPlugin,
  NotificationsPluginState,
  ScheduledNotification,
} from "@pimo/notifications-plugin-v2";
import {
  App,
  composeInitialState,
  LoginPlugin,
  LoginPluginPartialAppState,
  UniversalAppState,
} from "@pimo/pimo-app-builder";
import {
  Chatbot,
  DefaultOverlay,
  DefaultOverlayNotificationPayload,
  DefaultOverlayPartialAppState,
  DragDropLayoutState,
  DragDropLayoutTabDefinition,
  initialDefaultOverlayState,
  Log,
  TableSortData,
} from "@pimo/pimo-components";
import {
  getLocalizedMessage,
  L10nPlugin,
  type SupportedLanguage,
} from "@pimo/pimo-l10n";
import {
  type LockDetail,
  ResourceLockPlugin,
} from "@pimo/resource-lock-plugin";
import { getDefaultStepsForRole } from "@pimo/tutorial";
import dayjs from "dayjs";
import React from "react";
import { generatePath, matchPath } from "react-router-dom";
import {
  BasicItem,
  BffDashboard,
  BffOverlay,
  BffOverview,
  BffReport,
  ColumnDefinition,
  Configuration,
  ExtendedFieldError,
  Profile,
  Project,
  ReportingItem,
} from "vmd-types";
import {
  canUserPerformOperation,
  determineUserRole,
  validateConfiguration,
} from "vmd-utils";

import { APP_KEY } from "../app-key";
import { NotFoundPage } from "./components/not-found-page";
import { FilterData } from "./components/project-filter-dialog";
import { TrialExpiredModal } from "./components/trial-expired-modal";
import { UpdatesView } from "./components/updates-view/updates-view";
import {
  API_ROUTES,
  APP_ROUTES,
  RESOURCE_LOCK_POLLING_INTERVAL,
} from "./constants";
import { STRAPI_URL } from "./env";
import { fetchChatbotStatus } from "./helpers/fetch-chatbot-status";
import { fetchDashboard } from "./helpers/fetch-dashboard";
import { fetchEntityConfiguration } from "./helpers/fetch-entity-configurations";
import { fetchOverlay } from "./helpers/fetch-overlay";
import { fetchOverview } from "./helpers/fetch-overview";
import { fetchAllProjects, fetchProject } from "./helpers/fetch-project";
import { fetchSettings } from "./helpers/fetch-settings";
import { fetchTableConfiguration } from "./helpers/fetch-table-configuration";
import {
  fetchUserSettings,
  saveUserSettings,
  UserSettings,
} from "./helpers/fetch-user-settings";
import {
  fetchViewConfiguration,
  mapViewConfiguration,
} from "./helpers/fetch-view-configuration";
import de from "./locales/de.json";
import en from "./locales/en.json";
import { DashboardPlugin } from "./pages/dashboard-plugin";
import { HomePlugin } from "./pages/home-plugin";
import { LogsPlugin } from "./pages/logs-plugin";
import { OverviewPlugin } from "./pages/overview-plugin";
import { FindingsPlugin } from "./pages/report/findings-plugin";
import { ReportPlugin } from "./pages/report-plugin";
import { SettingsPlugin } from "./pages/settings-plugin";
import { theme } from "./theme";
import { getLockedByMessage } from "./utils/get-locked-by-message";
import { isTrialExpired } from "./utils/is-trial-expired";
import {
  ChannelStatsSortData,
  ReportingItemSortableFields,
} from "./utils/sort-table-data";

export type TestAppState = DefaultOverlayPartialAppState &
  LoginPluginPartialAppState<Profile> &
  UniversalAppState &
  NotificationsPluginState &
  DefaultOverlayPartialAppState & {
    number1: number;
    number2: number;
    editFormLoading?: boolean;
    dashboardLayout?: DragDropLayoutState;
    overviewLayout?: DragDropLayoutState;
    reportLayout?: DragDropLayoutState;
    isEditingReportLayout?: boolean;
    overlay?: BffOverlay;
    currentReport?: BffReport;
    dashboard?: BffDashboard;
    overview?: BffOverview & {};
    projects?: Project[];
    settings?: {
      projects?: Project[];
      basicItems?: BasicItem[];
      configuration?: Configuration;
    };
    editSettingsForm?: {
      configuration?: Configuration;
      basicItems?: BasicItem[];
      projects?: Project[];
      validationErrors?: Record<string, ExtendedFieldError>;
      deletedProjects?: number[];
      deletedBasicItems?: number[];
      users?: Profile[];
      enableSaveButton?: boolean;
      hasUpdatedUsers?: boolean;
      clientUsers?: Profile[];
      toolUsers?: Profile[];
      hasCreatedFields?: boolean;
      hasDeletedFields?: boolean;
    };
    editForm?: {
      currentTrackingItem?: BasicItem & { distributionChannel?: string[] };
      currentProject?: Project;
      currentReportingItem?: ReportingItem;
    };
    changedNotifications?: Notification[];
    isChatbotEnabled?: boolean;
    changedScheduledNotifications?: ScheduledNotification[];
    //
    logs?: Log[];
    uniqueEditors?: string[];
    logsSearch?: {
      fromDate?: string | null;
      untilDate?: string | null;
      selectedProject?: string;
      selectedEditor?: string;
      searchTerm?: string;
      projects?: Array<{ id: string; name: string }>;
      uniqueEditors?: Array<{ id: string; name: string }>;
    };
    activeMenu?: string | null;
    isLoading: boolean;
    isSidebarOpen: boolean;
    currentviewname?: string;
    dashboardComponentValues?: Record<string, unknown>;
    reportComponentValues?: Record<string, unknown>;
    language: SupportedLanguage;
    reportPluginSortData: TableSortData<ReportingItemSortableFields>;
    overviewPluginSortData: TableSortData<Project>;
    dashboardPluginSortData: ChannelStatsSortData;
    reportPluginColumnOrder?: number[];
    overviewPluginColumnOrder?: number[];
    overviewItSystemsColumnOrder?: number[];
    reportTablePagination?: {
      page: number;
      pageSize: 10 | 25 | 50 | 100;
    };
    overviewTablePagination?: {
      page: number;
      pageSize: 10 | 25 | 50 | 100;
    };
    overviewItSystemsTablePagination?: {
      page: number;
      pageSize: 10 | 25 | 50 | 100;
    };
    dashboardTablePagination?: {
      page: number;
      pageSize: 10 | 25 | 50 | 100;
    };
    filterData?: FilterData;
    itSystemsOverviewFilterData?: FilterData;
    importDialog?: {
      excelHeaders: string[];
      itemFields: string[];
      open: boolean;
      file: File | null;
      suggestedMapping: Record<string, string>;
      isLoading: boolean;
      excelData: unknown[][];
    };
    reportTableConfiguration?: {
      isEditMode: boolean;
      tableHeaderEntries: ColumnDefinition[];
      confirmationPopupType?: "save" | "abort";
      isDirty?: boolean;
    };
    overviewTableConfiguration?: {
      isEditMode: boolean;
      tableHeaderEntries: ColumnDefinition[];
      confirmationPopupType?: "save" | "abort";
      isDirty?: boolean;
    };
    itSystemsOverviewTableConfiguration?: {
      isEditMode: boolean;
      tableHeaderEntries: ColumnDefinition[];
      confirmationPopupType?: "save" | "abort";
      isDirty?: boolean;
    };
    // Confirmation dialog flags for delete actions
    showDeleteProjectDialog?: boolean;
    pendingDeleteProjectId?: string | number | undefined;
    showDeleteTrackingItemDialog?: boolean;
    pendingDeleteTrackingItemId?: string | number | undefined;
    showDeleteAppDialog?: boolean;
    pendingDeleteAppId?: string | number | undefined;
    // Confirms deleting the item currently open in the questionnaire dialog,
    // available only to the OE user who manually created it themselves.
    showDeleteOwnTrackingItemDialog?: boolean;
    showChannelDeletionDialog?: boolean;
    pendingChannelDeletionCount?: number;
    // OE picker dialog for creating a new tracking item from the System Overview table
    showCreateTrackingItemOeDialog?: boolean;
    selectedOeIdForNewTrackingItem?: string;
    // Confirmation dialog for saving report layout changes
    showConfirmSaveReportLayoutDialog?: boolean;
    showClearAllDataDialog?: boolean;
    showUnsavedSettingsDialog?: boolean;
    showModifiedFieldsDialog?: boolean;
    pendingSaveReportLayout?: {
      tabs: DragDropLayoutTabDefinition[];
      displayTabs: boolean;
      type?: "overview" | "dashboard" | "project-common";
    };
    // Confirmation dialog for saving overview layout changes
    showConfirmSaveOverviewLayoutDialog?: boolean;
    pendingSaveOverviewLayout?: {
      tabs: DragDropLayoutTabDefinition[];
      displayTabs: boolean;
      type?: "overview" | "dashboard" | "project-common";
    };
    // Tracking items details modal
    showTrackingItemDetailsModal?: boolean;
    selectedTrackingItem?: ReportingItem;
    userSettings: UserSettings;
    showEditFormConfirmationDialog?: boolean;
    showTrialExpiredDialog?: boolean;
    lockDetails?: {
      settingsLock?: LockDetail;
      projectLocks?: Record<
        string,
        { edit: Partial<LockDetail>; update: Partial<LockDetail> }
      >;
      itemLocks?: Record<string, Partial<LockDetail>>;
    };
  };

// Plugins
const loginPlugin = new LoginPlugin<Profile, TestAppState>(
  APP_ROUTES.dashboard,
  STRAPI_URL,
  "vmd",
  {
    redirectToBackendLoginIfLoggedOut: true,
  }
);

loginPlugin.on("login-status-changed", async (evt) => {
  if (!evt.payload?.isLoggedIn) {
    return;
  }
  const user = app.getAppState().userProfile;
  const language = app.getAppState().language || "en";

  const [
    settings,
    config,
    userSettings,
    allNotifications,
    scheduledNotifications,
    settingsLockDetail,
    isChatbotEnabled,
  ] = await Promise.all([
    fetchSettings(language),
    fetchEntityConfiguration(),
    fetchUserSettings(),
    notificationsPlugin.fetchAllNotifications(),
    notificationsPlugin.fetchScheduledNotifications(),
    resourceLockPlugin.isLocked("settings"),
    fetchChatbotStatus(),
  ]);

  const trialExpired = await isTrialExpired(config?.homePortalAppId);
  app.patchAppState({
    settings: {
      projects: settings.projects,
      basicItems: settings.basicItems ?? [],
      configuration: settings.configuration || config,
    },
    editSettingsForm: {
      projects: settings.projects,
      basicItems: settings.basicItems ?? [],
      configuration: settings.configuration || config,
      clientUsers: settings.clientUsers,
      toolUsers: settings.toolUsers,
    },
    userSettings: userSettings ?? {
      tutorialCompleted: true,
    },
    lockDetails: {
      ...app.getAppState().lockDetails,
      settingsLock: settingsLockDetail,
    },
    allNotifications: allNotifications,
    scheduledNotifications: scheduledNotifications,
    showTrialExpiredDialog: trialExpired ?? false,
    isChatbotEnabled,
  });

  if (
    canUserPerformOperation({
      userGroups: user?.groups || [],
      operation: "read",
      lowestLevelAccessRequiredToPerformThisOperation: "program",
      client: config?.client,
      tool: config?.tool,
    })
  ) {
    app.navigate(
      window.location.pathname === "/"
        ? APP_ROUTES.dashboard
        : window.location.pathname
    );
  } else {
    const projects = await fetchAllProjects();
    app.navigate(
      generatePath(APP_ROUTES.report, {
        id: projects?.[0].id ?? "",
        viewname: "dashboard",
      })
    );
  }
});
const homePlugin = new HomePlugin<TestAppState>();
const dashboardPlugin = new DashboardPlugin();
const reportPlugin = new ReportPlugin();
const findingsPlugin = new FindingsPlugin();
const overviewPlugin = new OverviewPlugin();
const settingsPlugin = new SettingsPlugin();
const logsPlugin = new LogsPlugin();
const notificationsPlugin = new NotificationsPlugin<TestAppState>(STRAPI_URL);
const resourceLockPlugin = new ResourceLockPlugin<TestAppState>(STRAPI_URL);

// Register locales from JSON files
const app = App.create<TestAppState>(
  composeInitialState<TestAppState>({
    ...initialDefaultOverlayState,
    ...loginPlugin.getInitialState(),
    isLoading: false,
    isSidebarOpen: true,
    number1: 1,
    number2: 2,
    reportPluginSortData: { key: "criticality", direction: "DESC" },
    overviewPluginSortData: { key: "customId", direction: "ASC" },
    dashboardPluginSortData: { key: "totalSystems", direction: "DESC" },
    reportTablePagination: { page: 1, pageSize: 25 },
    overviewTablePagination: { page: 1, pageSize: 25 },
    overviewItSystemsTablePagination: { page: 1, pageSize: 25 },
    dashboardTablePagination: { page: 1, pageSize: 25 },
    itSystemsOverviewFilterData: undefined,
    experimentalFeatures: {
      componentStateHydration: true,
    },
    language: "en" as SupportedLanguage,
    userSettings: {
      tutorialCompleted: true,
    },
  }),
  APP_KEY
);
app.setTheme(theme);

app.on("init", () => {
  setInterval(async () => {
    const appState = app.getAppState();

    const [settingsLock, lockedProjects] = await Promise.all([
      resourceLockPlugin.isLocked("settings"),
      resourceLockPlugin.getLockedProjects(
        (appState.projects ?? []).map(({ id }) => id)
      ),
    ]);
    const lockedItems = await resourceLockPlugin.getLockedItems(
      appState.projects?.reduce((acc, project) => {
        const reportingItems = project.reportingItems ?? [];
        const itemIds = reportingItems.map((item) => `item-${item.id}`);
        return [...acc, ...itemIds];
      }, [] as string[]) ?? []
    );

    app.patchAppState({
      lockDetails: {
        settingsLock,
        projectLocks: lockedProjects,
        itemLocks: lockedItems,
      },
    });
  }, RESOURCE_LOCK_POLLING_INTERVAL);
});

// Register enhanced L10nPlugin with locales and language detection
const l10nPlugin = new L10nPlugin<TestAppState>({
  supportedLanguages: ["en"], // "de" temporarily disabled
  defaultLanguage: "en",
  // detectBrowserLanguage: true, // Disabled - default language is English
  detectBrowserLanguage: false,
  storageKey: "language",
  locales: {
    en,
    de,
  },
});
app.registerPlugin(l10nPlugin);

const syncDayjsLocale = (language?: string) => {
  dayjs.locale((language || "en").toLowerCase().startsWith("de") ? "de" : "en");
};
syncDayjsLocale(app.getAppState().language);
app.on("state:changed", () => {
  syncDayjsLocale(app.getAppState().language);
});

// Overlay
const overlayView = app.createOverlayView({ name: "Overlay" });

const trialExpiredModal = overlayView.addComponent({
  component: TrialExpiredModal,
});

trialExpiredModal.mapState(({ showTrialExpiredDialog, language }) => ({
  open: showTrialExpiredDialog ?? false,
  language: language || "en",
}));

const pageOverlay = overlayView.addComponent({
  component: DefaultOverlay,
});

pageOverlay.on("overlay:show-logs", () => {
  app.navigate(APP_ROUTES.logs);
});

pageOverlay.on("overlay:notification-read", (params) => {
  const notificationsPlugin = app.getPluginByName<
    NotificationsPlugin<TestAppState>
  >("NotificationsPlugin");
  void notificationsPlugin?.handleOverlayNotificationReadEvent(
    params as { payload: DefaultOverlayNotificationPayload }
  );
});

pageOverlay.mapState(
  ({
    isSidebarOpen,
    userProfile,
    overlay,
    language,
    settings,
    snackbar,
    banner,
    userSettings,
    projects,
    lockDetails,
  }) => {
    // Temporarily disabled until VMD tutorial steps are fully defined.
    const isTutorialEnabled = false;
    const settingsLockDetail = lockDetails?.settingsLock;
    const isLocked =
      settingsLockDetail?.isLocked &&
      settingsLockDetail.lockedBy !== userProfile?.email;

    const userRole = determineUserRole(
      userProfile,
      settings?.configuration?.tool
    );

    const tutorialSteps = userRole
      ? getDefaultStepsForRole(
          userRole,
          projects?.[0]
            ? [{ replaceToken: "{id}", value: projects[0]?.id }]
            : undefined
        )
      : [];

    const shouldShowTutorial =
      isTutorialEnabled &&
      userSettings != null &&
      !userSettings.tutorialCompleted;
    const reportProjects =
      projects && projects.length > 0 ? projects : (overlay?.projects ?? []);

    return {
      snackbar,
      buttonProps: canUserPerformOperation({
        userGroups: userProfile?.groups || [],
        operation: "edit",
        object: "project",
        lowestLevelAccessRequiredToPerformThisOperation: "program",
        client: settings?.configuration?.client,
        tool: settings?.configuration?.tool,
      })
        ? {
            size: "medium",
            variant: "outlined",
            icon: <Add />,
            tourid: "new-project",
            text: `${getLocalizedMessage(language || "en", "addNew")} ${
              settings?.configuration?.projectName ??
              getLocalizedMessage(language || "en", "project")
            }`,
            onClick: () => {
              app?.navigate(
                generatePath(APP_ROUTES.projectDialog, {
                  id: "new-project",
                  isFromOverview: "true",
                })
              );
            },
          }
        : undefined,
      enableLogs: true,
      logs: [],
      hasSearchFunctionality: true,

      menuEntries: [
        ...(canUserPerformOperation({
          userGroups: userProfile?.groups || [],
          operation: "read",
          lowestLevelAccessRequiredToPerformThisOperation: "program",
          client: settings?.configuration?.client,
          tool: settings?.configuration?.tool,
        })
          ? [
              {
                title:
                  getLocalizedMessage(language || "en", "dashboard") ||
                  "Program Dashboard",
                tourid: "dashboard",
                link: `${APP_ROUTES.dashboard}`,
                icon: SpaceDashboardOutlined,
                level: 0,
                active: !!matchPath(
                  { path: APP_ROUTES.dashboard, end: true }, // ← exact match
                  window.location.pathname
                ),
              },
            ]
          : []),
        ...(canUserPerformOperation({
          userGroups: userProfile?.groups || [],
          operation: "read",
          lowestLevelAccessRequiredToPerformThisOperation: "program",
          client: settings?.configuration?.client,
          tool: settings?.configuration?.tool,
        })
          ? [
              {
                title: `${getLocalizedMessage(language || "en", "overview") || "Overview"}`,
                link: `${APP_ROUTES.overview}`,
                icon: ListAltRounded,
                level: 0,
              },
            ]
          : []),
        {
          title: getLocalizedMessage(language || "en", "reports") || "OEs",
          tourid: "project-reports",
          level: 0,
          icon: AssessmentOutlined,
          items: reportProjects.map((entry) => ({
            level: 1,
            link: generatePath(APP_ROUTES.report, {
              id: String(entry.id),
              viewname: "dashboard",
            }),
            active: window.location.pathname.includes(
              `reports/${entry.id ?? ""}/`
            ),
            title: `${entry.customId ? entry.customId + " - " : ""}${
              entry.name
            }`,
          })),
          // highlight the parent “Reports” heading whenever you’re on any report
          active: window.location.pathname.includes(`reports/`),
        },
      ],

      header: {
        logo: {
          big: settings?.configuration?.logo ?? "allianz.svg",
          small: settings?.configuration?.logo ?? "allianz.svg",
          minWidth: 100,
          maxWidth: 130,
        },
        username: userProfile?.email,
        fullName: userProfile?.name,
        actionButtons: [
          createContactButton(),
          createNotificationsButton(app.getAppState(), app),
          createUpdatesButton(app.getAppState(), app),

          ...(canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            operation: "read",
            lowestLevelAccessRequiredToPerformThisOperation: "program",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          })
            ? [
                {
                  icon: PublishedWithChanges,
                  onClick: () => {
                    pageOverlay.fireEvent("overlay:show-logs");
                  },
                },
              ]
            : []),

          ...(canUserPerformOperation({
            userGroups: userProfile?.groups || [],
            lowestLevelAccessRequiredToPerformThisOperation: "admin",
            client: settings?.configuration?.client,
            tool: settings?.configuration?.tool,
          })
            ? [
                {
                  icon: isLocked ? Lock : SettingsOutlined,
                  disabled: isLocked,
                  onClick: () => {
                    resourceLockPlugin.lock("settings");
                    app?.navigate(APP_ROUTES.settings.reportingStructure);
                    app.patchAppState({ isSidebarOpen: false });
                  },
                  tooltip: getLockedByMessage(
                    "settingsPage",
                    language,
                    settingsLockDetail
                  ),
                  text:
                    getLocalizedMessage(language || "en", "settings") ||
                    "Reporting Settings",
                },
              ]
            : []),

          // Hide download button until a proper download template is implemented
          // ...(canUserPerformOperation({
          //   userGroups: userProfile?.groups || [],
          //   lowestLevelAccessRequiredToPerformThisOperation: "admin",
          //   client: settings?.configuration?.client,
          //   tool: settings?.configuration?.tool,
          // })
          //   ? [
          //       {
          //         icon: FileDownloadOutlined,
          //         menu: {
          //           items: [
          //             {
          //               label: "Download Excel",
          //               onClick: () => downloadExcelExport(),
          //             },
          //           ],
          //         },
          //       },
          //     ]
          //   : []),
        ],
        entries: [
          // {
          //   text:
          //     getLocalizedMessage(
          //       language || "en",
          //       "userMenu.contactPimoTeam"
          //     ) || "Contact Pimo team",
          //   onClick: () => {
          //     window.open("mailto:contact@pimoapp.com");
          //   },
          //   icon: Email,
          // },
          // {
          //   text: `${
          //     getLocalizedMessage(language || "en", "userMenu.language") ||
          //     "Language"
          //   }: ${getLocalizedMessage(
          //     language || "en",
          //     `settingsPage.${language === "en" ? "english" : "german"}`
          //   )}`,
          //   menu: {
          //     onChange: (value) => {
          //       app.patchAppState({ language: value });
          //     },
          //     items: [
          //       {
          //         label:
          //           getLocalizedMessage(
          //             language || "en",
          //             "settingsPage.german"
          //           ) ?? "German",
          //         value: "de",
          //       },
          //       {
          //         label:
          //           getLocalizedMessage(
          //             language || "en",
          //             "settingsPage.english"
          //           ) ?? "English",
          //         value: "en",
          //       },
          //     ],
          //   },
          //   icon: Language,
          // },
          // {
          //   text:
          //     getLocalizedMessage(
          //       language || "en",
          //       "userMenu.homeportalLink"
          //     ) || "To Home Portal",
          //   onClick: () => {
          //     window.open("https://home.pimoapp.com/home-portal");
          //   },
          //   icon: Inventory,
          // },
          {
            text: getLocalizedMessage(language || "en", "logout") || "Logout",
            onClick: () => {
              loginPlugin.logout();
            },
            icon: Logout,
          },
        ],
      },
      isSidebarOpen,
      notifications: {
        banner,
      },
      tutorial: {
        welcomeText:
          "Welcome to Pimo. Your reporting tool is now ready. Would you like to complete the tutorial first or finish setting up reporting right away?",
        welcomeLogo: "allianz.svg",
        steps: tutorialSteps,
        showTutorial: shouldShowTutorial ?? false,
        setTutorialCompleted: () => {
          app.patchAppState({
            userSettings: {
              ...app.getAppState().userSettings,
              tutorialCompleted: true,
            },
          });
          saveUserSettings({ tutorialCompleted: true });
        },
      },
    };
  }
);

const chatbotComponent = overlayView.addComponent({ component: Chatbot });
chatbotComponent.mapState(({ isChatbotEnabled }) => ({
  url: `${STRAPI_URL.replace(/\/$/, "")}/${API_ROUTES.CHAT}`,
  show: isChatbotEnabled ?? false,
}));

const createUpdatesView = (state: TestAppState) => ({
  id: "updates",
  content: React.createElement(UpdatesView, {
    projects: state?.projects || [],
    reportingDate:
      state.settings?.configuration?.reportingCycle?.endReportingCycleDate ||
      "",
  }),
});

const CONTACT_EMAIL = "cailin.van-der-zijden@allianz.de";
const CONTACT_SUBJECT = "Pimo - SDC user question";
const CONTACT_LABEL = "Feedback & Support";

const createContactButton = () => ({
  icon: MailOutline,
  text: CONTACT_LABEL,
  ariaLabel: CONTACT_LABEL,
  onClick: () => {
    const subject = encodeURIComponent(CONTACT_SUBJECT);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}`;
  },
  tooltip: CONTACT_LABEL,
});

const createUpdatesButton = (state: TestAppState, app: App<TestAppState>) => {
  return {
    icon: SentimentSatisfiedAlt,
    badgeValue: state?.projects?.length || 0,
    onClick: () => app.patchAppState({ activeMenu: "updates" }),
    actionSidebar: {
      items: [createUpdatesView(state)],
      isOpen: state.activeMenu === "updates",
      onClose: () => app.patchAppState({ activeMenu: null }),
      title: "Reporting Updates",
    },
  };
};

const createNotificationsButton = (
  state: TestAppState,
  app: App<TestAppState>
) => ({
  icon: NotificationsOutlined,
  badgeValue:
    state.allNotificationsForUser?.filter(
      (n) =>
        n.channels?.includes(NotificationChannel.NOTIFICATION) &&
        n.active &&
        n.published
    ).length || 0,
  onClick: () => app.patchAppState({ activeMenu: "notifications" }),
  actionSidebar: {
    items: generateNotificationItems(state, app),
    isOpen: state.activeMenu === "notifications",
    onClose: () => app.patchAppState({ activeMenu: null }),
    title: "Notifications",
  },
});

const generateNotificationItems = (
  state: TestAppState,
  app: App<TestAppState>
) => {
  const notifications =
    state.allNotificationsForUser?.filter(
      (n) =>
        n.channels?.includes(NotificationChannel.NOTIFICATION) &&
        n.active &&
        n.published
    ) ?? [];

  if (notifications.length === 0) return [];

  return notifications.map((notification) => ({
    id: notification.id ?? 0,
    notification: {
      id: notification.id ?? 0,
      title: notification.title,
      text: notification.text,
      createdAt: notification.createdAt,
      isRead: notification.isRead,
    },
    onClick: () => {
      const notificationsPlugin = app.getPluginByName<
        NotificationsPlugin<TestAppState>
      >("NotificationsPlugin");
      if (notificationsPlugin && notification.id) {
        void notificationsPlugin.handleOverlayNotificationReadEvent({
          payload: { notification },
        });
      }
    },
  }));
};

pageOverlay.on("overlay:toggle-sidebar", () => {
  app.patchAppState({
    isSidebarOpen: !app.getAppState().isSidebarOpen,
  });
});

app.registerPlugin(loginPlugin);
app.registerPlugin(homePlugin);
app.registerPlugin(dashboardPlugin);
app.registerPlugin(overviewPlugin);
app.registerPlugin(reportPlugin);
app.registerPlugin(findingsPlugin);
app.registerPlugin(notificationsPlugin);
app.registerPlugin(settingsPlugin);
app.registerPlugin(logsPlugin);
app.registerPlugin(resourceLockPlugin);

const notFoundView = app.createView({ name: "not-found" });
const notFoundPage = notFoundView.addComponent({ component: NotFoundPage });
notFoundPage.mapState(({ language, settings, userProfile }) => ({
  language,
  canViewPrograms: canUserPerformOperation({
    userGroups: userProfile?.groups || [],
    operation: "read",
    lowestLevelAccessRequiredToPerformThisOperation: "program",
    client: settings?.configuration?.client,
    tool: settings?.configuration?.tool,
  }),
}));
notFoundPage.on("not-found:navigate", ({ payload }) => {
  if (payload?.path) app.navigate(payload.path);
});
// A page opened in a fresh tab has no history to go back to.
notFoundPage.on("not-found:back", () => {
  if (window.history.length > 1) window.history.back();
  else app.navigate(APP_ROUTES.home);
});
app.createRoute({ path: "*", view: notFoundView, isOverlayChild: false });

overviewPlugin.route?.on("load", async () => {
  const [
    overview,
    overlay,
    viewConfiguration,
    tableConfiguration,
    itSystemsOverviewTableConfiguration,
  ] = await Promise.all([
    fetchOverview(),
    fetchOverlay(),
    fetchViewConfiguration("overview"),
    fetchTableConfiguration("projectsTableComponent"),
    fetchTableConfiguration("itSystemsOverviewTableComponent"),
  ]);

  const projects = overview?.projects;

  const lockedProjects = await resourceLockPlugin.getLockedProjects(
    (overview?.projects ?? []).map((project) => `project-${project.id}`)
  );

  app.patchAppState({
    ...(overview
      ? {
          overview,
        }
      : {}),
    overlay,
    projects,
    editForm: {},
    lockDetails: {
      ...app.getAppState().lockDetails,
      projectLocks: lockedProjects,
    },
    overviewTableConfiguration: {
      isEditMode: false,
      tableHeaderEntries: tableConfiguration?.columns ?? [],
      confirmationPopupType: undefined,
    },
    itSystemsOverviewTableConfiguration: {
      isEditMode: false,
      tableHeaderEntries: itSystemsOverviewTableConfiguration?.columns ?? [],
      confirmationPopupType: undefined,
      isDirty: false,
    },
    itSystemsOverviewFilterData: undefined,
    ...(viewConfiguration
      ? {
          overviewLayout: mapViewConfiguration(viewConfiguration),
        }
      : {}),
  });
});

dashboardPlugin.route?.on("load", async () => {
  const [
    dashboard,
    overlay,
    viewConfiguration,
    configuration,
    settings,
    settingsLockDetail,
  ] = await Promise.all([
    fetchDashboard(),
    fetchOverlay(),
    fetchViewConfiguration("dashboard"),
    fetchEntityConfiguration(),
    fetchSettings(),
    resourceLockPlugin.isLocked("settings"),
  ]);

  const projects = dashboard?.projects;

  app.patchAppState({
    isSidebarOpen: true,
    dashboard,
    filterData: undefined,
    overlay,
    projects,
    settings: { ...settings, configuration: configuration },
    editSettingsForm: {
      ...settings,
      configuration: configuration,
    },
    lockDetails: {
      ...app.getAppState().lockDetails,
      settingsLock: settingsLockDetail,
    },
    ...(viewConfiguration
      ? {
          dashboardLayout: mapViewConfiguration(viewConfiguration),
        }
      : {}),
  });
});

reportPlugin.route?.on("load", async ({ payload }) => {
  const params = payload?.parameters;
  if (!params?.id) {
    return;
  }

  const [
    currentReport,
    overlay,
    viewConfiguration,
    projects,
    tableConfiguration,
  ] = await Promise.all([
    fetchProject({ id: params.id }),
    fetchOverlay(),
    fetchViewConfiguration("project-common"),
    fetchAllProjects(),
    fetchTableConfiguration("trackingItemsTableComponent"),
  ]);

  const [lockedItems, lockedProjects] = await Promise.all([
    resourceLockPlugin.getLockedItems(
      (currentReport?.project?.reportingItems ?? []).map(
        (item) => `item-${item.id}`
      )
    ),
    resourceLockPlugin.getLockedProjects((projects ?? []).map(({ id }) => id)),
  ]);

  if (!app) {
    return;
  }
  const appState = app.getAppState();

  let userEmail = appState.userProfile?.email;
  // In some cases, when opening the report page directly, the user profile might not be immediately available.
  // We wait for a short time and check again to ensure we have the user's email before determining the lock status.
  if (!userEmail) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    userEmail = appState.userProfile?.email;
  }

  app.patchAppState({
    currentReport,
    lockDetails: {
      ...appState.lockDetails,
      itemLocks: lockedItems,
      projectLocks: lockedProjects,
    },
    overlay,
    filterData: undefined,
    projects,
    ...(viewConfiguration
      ? {
          reportLayout: mapViewConfiguration(viewConfiguration),
        }
      : {}),
    reportTableConfiguration: {
      isEditMode: false,
      tableHeaderEntries: tableConfiguration?.columns ?? [],
      confirmationPopupType: undefined,
      isDirty: false,
    },
    // Reset in case a previous visit left layout-edit mode active without
    // ever firing "stopLayoutEdit" (e.g. the user navigated away mid-edit),
    // which would otherwise permanently hide the Findings stub tab.
    isEditingReportLayout: false,
  });
});

findingsPlugin.route?.on("load", async ({ payload }) => {
  const params = payload?.parameters;
  if (!params?.id) {
    return;
  }

  const [currentReport, overlay, viewConfiguration] = await Promise.all([
    fetchProject({ id: params.id }),
    fetchOverlay(),
    fetchViewConfiguration("project-common"),
  ]);

  if (!app) {
    return;
  }

  app.patchAppState({
    currentReport,
    overlay,
    filterData: undefined,
    ...(viewConfiguration
      ? { reportLayout: mapViewConfiguration(viewConfiguration) }
      : {}),
    isEditingReportLayout: false,
  });
});

settingsPlugin.route?.on("load", async () => {
  app.patchAppState({
    isLoading: true,
  });
  const language = app.getAppState().language || "en";
  const settings = await fetchSettings(language);
  if (!app) {
    return;
  }

  app.patchAppState({
    isLoading: false,
    filterData: undefined,
    editSettingsForm: {
      projects: settings.projects,
      basicItems: settings.basicItems ?? [],
      configuration: settings.configuration,
      toolUsers: settings.toolUsers,
      clientUsers: settings.clientUsers,

      validationErrors: validateConfiguration(
        settings.configuration,
        settings.projects ?? [],
        settings.basicItems ?? [],
        settings.toolUsers ?? [],
        settings.clientUsers ?? [],
        {
          fieldRequired:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
          dateNotValid:
            getLocalizedMessage(
              language || "en",
              "validationErrors.dateNotValid"
            ) || "This date is not valid",
          endDateAfterStartDate:
            getLocalizedMessage(
              language || "en",
              "validationErrors.endDateAfterStartDate"
            ) || "End date should be after start date",
        }
      ),
      enableSaveButton: false,
    },
    settings: settings,
  });
});
const RenderedApp = app.render();

export default RenderedApp;
